import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig";
import {
  Box, Button, TextField, Typography, Select, MenuItem
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import { handleApiError } from "../utils/apiErrorHandler";

interface Judge {
  _id: string;
  firstname: string;
  surname: string;
  license: string;
}

const JudgesManagementPage: React.FC = () => {
  const [judges, setJudges] = useState<Judge[]>([]);
  const [newJudge, setNewJudge] = useState<Judge>({
    _id: "",
    firstname: "",
    surname: "",
    license: "C-Lizenz",
  });
  const [editedRows, setEditedRows] = useState<{ [key: string]: boolean }>({});
  const apiRoute = "/api/judges";

  // 🔹 Kampfrichter laden
  useEffect(() => {
    fetchJudges();
  }, []);

  const fetchJudges = async () => {
    try {
      const response = await axios.get(apiRoute);
      setJudges(response.data);
    } catch (error: any) {
      handleApiError(error, "Fehler beim Laden der Kampfrichter.");
    }
  };

  // 🔹 Kampfrichter hinzufügen
  const addJudge = async () => {
    try {
      await axios.post(apiRoute, newJudge);
      setNewJudge({ _id: "", firstname: "", surname: "", license: "C-Lizenz" });
      fetchJudges();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Hinzufügen eines Kampfrichters.");
    }
  };

  // 🔹 Kampfrichter löschen
  const deleteJudge = async (judgeId: string) => {
    try {
      await axios.delete(`${apiRoute}/${judgeId}`);
      fetchJudges();
    } catch (error: any) {
     handleApiError(error, "Fehler beim Löschen eines Kampfrichters.");
    }
  };

  // 🔹 Änderungen speichern
  const saveRow = async (judge: Judge) => {
    try {
      await axios.put(`${apiRoute}/${judge._id}`, judge);
      setEditedRows((prev) => ({ ...prev, [judge._id]: false })); // Markierung entfernen
      fetchJudges();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Speichern der Änderung(en).");
    }
  };

  // 🔹 Spalten für die DataGrid-Tabelle
  const columns: GridColDef[] = [
    { field: "firstname", headerName: "Vorname", flex: 1, minWidth: 100, editable: true },
    { field: "surname", headerName: "Nachname", flex: 1, minWidth: 100, editable: true },
    {
      field: "license",
      headerName: "Lizenz",
      flex: 1,
      minWidth: 120,
      editable: true,
      type: "singleSelect",
      valueOptions: ["D-Lizenz", "C-Lizenz", "B-Lizenz", "A-Lizenz", "FIG Lizenz"],
    },
    {
      field: "actions",
      headerName: "Aktionen",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => saveRow(params.row)}
            disabled={!editedRows[params.row._id]}
          >
            Speichern
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => deleteJudge(params.row._id)}
            sx={{ ml: 1 }}
          >
            Löschen
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Kampfrichterverwaltung
      </Typography>

      {/* 🔹 Eingabefelder für neuen Kampfrichter */}
      <Box display="flex" gap={2} sx={{ mb: 2 }}>
        <TextField
          label="Vorname"
          value={newJudge.firstname}
          onChange={(e) => setNewJudge({ ...newJudge, firstname: e.target.value })}
        />
        <TextField
          label="Nachname"
          value={newJudge.surname}
          onChange={(e) => setNewJudge({ ...newJudge, surname: e.target.value })}
        />
        <Select
          value={newJudge.license}
          onChange={(e) => setNewJudge({ ...newJudge, license: e.target.value as string })}
        >
          <MenuItem value="D-Lizenz">D-Lizenz</MenuItem>
          <MenuItem value="C-Lizenz">C-Lizenz</MenuItem>
          <MenuItem value="B-Lizenz">B-Lizenz</MenuItem>
          <MenuItem value="A-Lizenz">A-Lizenz</MenuItem>
          <MenuItem value="FIG Lizenz">FIG Lizenz</MenuItem>
        </Select>
        <Button variant="contained" color="primary" onClick={addJudge}>
          Hinzufügen
        </Button>
      </Box>

      {/* 🔹 MUI DataGrid für Kampfrichter */}
      <Box sx={{ height: "auto", minHeight: 400, width: "100%", mt: 2 }}>
        <DataGrid
          rows={judges}
          columns={columns}
          getRowId={(row) => row._id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          processRowUpdate={(updatedRow) => {
            setEditedRows((prev) => ({ ...prev, [updatedRow._id]: true })); // Zeile als geändert markieren
            return updatedRow;
          }}
          sx={{
            height: "auto",
            minHeight: 400, // Mindesthöhe für gute Lesbarkeit
            maxHeight: "70vh", // Verhindert, dass die Tabelle zu groß wird
            "& .MuiDataGrid-row": {
              transition: "background-color 0.3s",
            },
            "& .MuiDataGrid-row.edited": {
              backgroundColor: "#fff3cd", // Hellgelb für geänderte Zeilen
            },
          }}
          getRowClassName={(params) => (editedRows[params.row._id] ? "edited" : "")}
        />
      </Box>
    </>
  );
};

export default JudgesManagementPage;